/**
 * One delivery explained for support (D-38): what happened, what to do, a readable timeline,
 * the content that was sent, and the raw technical details folded away.
 */
import { type DeliveryEvent, type DeliveryLogDetail, retryAction } from "@aiyo/shared";
import { ArrowRotateRight, ArrowUpRightFromSquare } from "@gravity-ui/icons";
import { Button, Disclosure, Drawer } from "@heroui/react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useFormat } from "../lib/format";
import { useLastDefined } from "../lib/hooks";
import { useDeliveryLogItem, useMe } from "../lib/queries";
import { IssueChip, IssueExplanation } from "./issues";
import { ChannelPreview } from "./previews";
import { RetryDialog } from "./RetryDialog";
import {
  ChannelChip,
  Code,
  CodeBlock,
  CopyButton,
  ErrorBlock,
  KeyValue,
  LoadingBlock,
  StateChip,
} from "./ui";

export function DeliveryDrawer({ deliveryId, onClose }: { deliveryId: string | null; onClose: () => void }) {
  const { t } = useTranslation();
  // Keep the last id while the drawer animates closed, so its content does not vanish mid-animation.
  const shownId = useLastDefined(deliveryId);
  const delivery = useDeliveryLogItem(shownId);
  const d = delivery.data;
  const { data: me } = useMe();
  const [retrying, setRetrying] = useState(false);
  const canRetry =
    me?.role !== "viewer" &&
    d !== undefined &&
    (d.status === "failed" || d.status === "skipped") &&
    retryAction(d.issue, Boolean(d.customer_id || d.external_id)) !== "never";

  return (
    <>
      <Drawer.Backdrop isOpen={deliveryId !== null} onOpenChange={(open) => !open && onClose()}>
        <Drawer.Content placement="right">
          {/* Inline width: the drawer's own width rule is more specific than a utility class. */}
          <Drawer.Dialog className="flex h-full flex-col" style={{ width: "36rem" }}>
            <Drawer.CloseTrigger />
            <Drawer.Header className="gap-2 pr-10">
              <Drawer.Heading className="flex flex-wrap items-center gap-3">
                {t("delivery.title")}
                {d ? <IssueChip issue={d.issue} /> : null}
              </Drawer.Heading>
              {d?.preview ? <p className="line-clamp-2 text-sm text-muted">{d.preview}</p> : null}
            </Drawer.Header>
            <Drawer.Body className="flex flex-col gap-6 text-foreground">
              {delivery.isPending ? (
                <LoadingBlock />
              ) : delivery.isError ? (
                <ErrorBlock error={delivery.error} onRetry={() => delivery.refetch()} />
              ) : d ? (
                <DeliveryBody d={d} />
              ) : null}
            </Drawer.Body>
            <Drawer.Footer>
              <Button slot="close" variant="secondary">
                {t("common.close")}
              </Button>
              {canRetry ? (
                <Button onPress={() => setRetrying(true)}>
                  <ArrowRotateRight />
                  {t("retry.one")}
                </Button>
              ) : null}
            </Drawer.Footer>
          </Drawer.Dialog>
        </Drawer.Content>
      </Drawer.Backdrop>
      {d ? (
        <RetryDialog isOpen={retrying} onOpenChange={setRetrying} selection={{ delivery_ids: [d.id] }} />
      ) : null}
    </>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function DeliveryBody({ d }: { d: DeliveryLogDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const payload = (d.rendered?.payload ?? null) as Record<string, unknown> | null;

  return (
    <>
      <IssueExplanation issue={d.issue} />

      <Section title={t("delivery.customer")}>
        <KeyValue
          items={[
            {
              label: t("delivery.customer"),
              value: d.external_id ? (
                <span className="flex flex-wrap items-center gap-2">
                  <Code>{d.external_id}</Code>
                  {d.customer_id ? (
                    <Link to={`/customers/${d.customer_id}`} className="text-sm text-accent hover:underline">
                      {t("delivery.openCustomer")}
                    </Link>
                  ) : null}
                </span>
              ) : (
                <span className="text-muted">{t("logs.directAddress")}</span>
              ),
            },
            {
              label: t("delivery.address"),
              value: d.address ? (
                <Code>{d.address}</Code>
              ) : (
                <span className="text-muted">{t("logs.noAddress")}</span>
              ),
            },
            ...(d.subscription
              ? [
                  {
                    label: t("delivery.subscription"),
                    value: (
                      <span className="flex flex-wrap items-center gap-2">
                        <StateChip
                          ok={
                            d.subscription.status === "active"
                              ? true
                              : d.subscription.status === "invalid"
                                ? "warn"
                                : false
                          }
                          label={t(`customers.subscriptionStatus.${d.subscription.status}`)}
                        />
                        {d.subscription.invalid_reason ? <Code>{d.subscription.invalid_reason}</Code> : null}
                      </span>
                    ),
                  },
                ]
              : []),
            { label: t("delivery.channel"), value: <ChannelChip channel={d.channel} /> },
            {
              label: t("delivery.app"),
              value: (
                <span className="flex flex-wrap items-center gap-2">
                  {d.app.name}
                  {d.test_mode ? <StateChip ok="warn" label={t("logs.test")} /> : null}
                </span>
              ),
            },
          ]}
        />
      </Section>

      <Section title={t("delivery.timeline")}>
        <Timeline d={d} />
      </Section>

      {payload ? (
        <Section title={t("delivery.content")}>
          <ChannelPreview channel={d.channel} payload={payload} to={d.address || null} appName={d.app.name} />
        </Section>
      ) : null}

      <Disclosure>
        <Disclosure.Heading>
          <Button slot="trigger" variant="ghost" size="sm" className="-ml-2">
            {t("delivery.technical")}
            <Disclosure.Indicator />
          </Button>
        </Disclosure.Heading>
        <Disclosure.Content>
          <Disclosure.Body className="flex flex-col gap-5 pt-3">
            <KeyValue
              items={[
                {
                  label: t("delivery.deliveryId"),
                  value: (
                    <span className="flex items-center gap-1">
                      <Code>{d.id}</Code>
                      <CopyButton value={d.id} />
                    </span>
                  ),
                },
                {
                  label: t("delivery.message"),
                  value: (
                    <span className="flex flex-wrap items-center gap-2">
                      <Code>{d.message_id}</Code>
                      <CopyButton value={d.message_id} />
                      <Link
                        to={`/apps/${d.app.id}/messages/${d.message_id}`}
                        className="inline-flex items-center gap-1 text-sm text-accent hover:underline"
                      >
                        {t("delivery.openMessage")}
                        <ArrowUpRightFromSquare className="size-3" />
                      </Link>
                    </span>
                  ),
                },
                { label: t("delivery.provider"), value: d.provider ?? "—" },
                { label: t("delivery.credential"), value: d.credential_alias ?? t("common.none") },
                { label: t("delivery.attempts"), value: d.attempts },
                {
                  label: t("delivery.providerMessageId"),
                  value: d.provider_message_id ? <Code>{d.provider_message_id}</Code> : "—",
                },
                ...(d.error_code
                  ? [
                      { label: t("delivery.errorCode"), value: <Code>{d.error_code}</Code> },
                      { label: t("delivery.errorMessage"), value: d.error_message ?? "—" },
                    ]
                  : []),
                { label: t("messages.createdAt"), value: fmt.dateTime(d.queued_at) },
              ]}
            />
            {d.events.length ? (
              <div className="flex flex-col gap-2">
                <div className="text-sm text-muted">{t("delivery.events")}</div>
                <CodeBlock value={JSON.stringify(d.events, null, 2)} />
              </div>
            ) : null}
            {d.rendered ? (
              <div className="flex flex-col gap-2">
                <div className="text-sm text-muted">{t("delivery.payload")}</div>
                <CodeBlock value={JSON.stringify(d.rendered, null, 2)} />
              </div>
            ) : null}
          </Disclosure.Body>
        </Disclosure.Content>
      </Disclosure>
    </>
  );
}

type Step = {
  key: string;
  label: string;
  at: string | null;
  tone: "success" | "danger" | "warning" | "default";
  note?: string;
};

function Timeline({ d }: { d: DeliveryLogDetail }) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const steps: Step[] = [
    { key: "created", label: t("delivery.steps.created"), at: d.queued_at, tone: "default" },
  ];
  for (const e of d.events) steps.push(stepFor(e, t));
  if (d.status === "skipped") {
    // The explanation above already says why; the raw server message would repeat it in English.
    steps.push({ key: "skipped", label: t("delivery.steps.skipped"), at: null, tone: "warning" });
  }

  return (
    <ol className="flex flex-col">
      {steps.map((s, i) => (
        <li key={s.key} className="relative flex gap-3 pb-4 last:pb-0">
          {i < steps.length - 1 ? (
            <span className="absolute top-4 bottom-0 left-[5px] w-px bg-separator" aria-hidden />
          ) : null}
          <span
            className={`relative mt-1.5 size-[11px] shrink-0 rounded-full ring-2 ring-surface ${
              s.tone === "success"
                ? "bg-success"
                : s.tone === "danger"
                  ? "bg-danger"
                  : s.tone === "warning"
                    ? "bg-warning"
                    : "bg-default-foreground/40"
            }`}
            aria-hidden
          />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="text-sm font-medium">{s.label}</span>
            <span className="text-xs text-muted">
              {s.at ? fmt.dateTime(s.at) : null}
              {s.note ? <span className="block break-words">{s.note}</span> : null}
            </span>
          </div>
        </li>
      ))}
    </ol>
  );
}

function stepFor(e: DeliveryEvent, t: (key: never, opts?: Record<string, unknown>) => string): Step {
  const p = (e.payload ?? {}) as Record<string, unknown>;
  const message = typeof p.message === "string" ? p.message : undefined;
  switch (e.type) {
    case "sent":
      return {
        key: e.id,
        label: t("delivery.steps.sent" as never),
        at: e.occurred_at,
        tone: "success",
        note: providerNote(p),
      };
    case "delivered":
      return { key: e.id, label: t("delivery.steps.delivered" as never), at: e.occurred_at, tone: "success" };
    case "retry_scheduled":
      return {
        key: e.id,
        label: t("delivery.steps.retry_scheduled" as never),
        at: e.occurred_at,
        tone: "warning",
        note: [
          message,
          typeof p.retry_in_sec === "number"
            ? t("delivery.retryIn" as never, { seconds: p.retry_in_sec })
            : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "retry_requested":
      return {
        key: e.id,
        label: t(
          (p.new_device ? "delivery.steps.retry_new_device" : "delivery.steps.retry_requested") as never,
        ),
        at: e.occurred_at,
        tone: "default",
        note: [
          typeof p.requested_by === "string" ? t("delivery.retryBy" as never, { who: p.requested_by }) : null,
          typeof p.address === "string" ? t("delivery.retryAddress" as never, { address: p.address }) : null,
          typeof p.credential_alias === "string"
            ? t("delivery.retryCredential" as never, { alias: p.credential_alias })
            : null,
        ]
          .filter(Boolean)
          .join(" · "),
      };
    case "failed":
      return {
        key: e.id,
        label: t("delivery.steps.failed" as never),
        at: e.occurred_at,
        tone: "danger",
        note: message,
      };
    default:
      return { key: e.id, label: t(`delivery.steps.${e.type}` as never), at: e.occurred_at, tone: "default" };
  }
}

function providerNote(p: Record<string, unknown>) {
  const parts = [p.provider, p.credential_alias].filter((v) => typeof v === "string" && v);
  return parts.length ? parts.join(" · ") : undefined;
}
